import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Input from "components/form/input/Input";
import Popup from "components/popup/Popup";
import Sheet from "components/sheet/Sheet";
import { PostEmailMarketingTemplate } from "api/rpc/2024-04/facilityAdmin/marketing/emailMarketingTemplates";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useStore } from "react-redux";
import type { RootState } from "index";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { emailBuilderCommitAndFlushDraft } from "../emailBuilderCommitAndFlushDraft";
import {
  EMAIL_BUILDER_TEMPLATE_NAME_MAX_LENGTH,
  validateEmailBuilderTemplateName,
} from "./emailBuilderSaveAsTemplateName";
import "./emailBuilderSaveAsTemplate.scss";
import { StatusCode } from "api/protocols";

interface IEmailBuilderSaveAsTemplateModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

function apiErrorMessage(message: unknown, fallback: string): string {
  if (typeof message === "string" && message.length > 0) {
    return message;
  }

  return fallback;
}

export function EmailBuilderSaveAsTemplateModal(props: IEmailBuilderSaveAsTemplateModalProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const windowSize = useWindowSize();
  const isMobile = (windowSize.width ?? MOBILE_WIDTH + 1) <= MOBILE_WIDTH;
  const campaignId = useAppSelector(state => state.emailBuilderStore.campaign?.id ?? null);

  const [formState, setFormState] = useState({
    open: false,
    name: "",
    validationError: null as string | null,
    saving: false,
  });
  if (props.open !== formState.open) {
    setFormState({
      open: props.open,
      name: "",
      validationError: null,
      saving: false,
    });
  }
  const { name, validationError, saving } = formState;
  const setName = (nextName: string) => {
    setFormState(current => ({ ...current, name: nextName }));
  };
  const setValidationError = (nextValidationError: string | null) => {
    setFormState(current => ({ ...current, validationError: nextValidationError }));
  };
  const setSaving = (nextSaving: boolean) => {
    setFormState(current => ({ ...current, saving: nextSaving }));
  };
  const isFlushingServerDraftRef = useRef(false);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!props.open) {
      return;
    }

    const active = window.document.activeElement;
    returnFocusRef.current = active instanceof HTMLElement ? active : null;

    const focusTimeoutId = window.setTimeout(() => {
      inputRef.current?.focus();
    }, 50);

    return () => {
      window.clearTimeout(focusTimeoutId);
    };
  }, [props.open]);

  useEffect(() => {
    if (props.open) {
      return;
    }

    const returnFocus = returnFocusRef.current;
    if (returnFocus !== null && typeof returnFocus.focus === "function") {
      returnFocus.focus();
    }
    returnFocusRef.current = null;
  }, [props.open]);

  useEffect(() => {
    if (!props.open || saving) {
      return;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        props.onClose();
      }
    }

    window.document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.document.removeEventListener("keydown", handleKeyDown);
    };
  }, [props.open, props.onClose, saving]);

  function handleClose() {
    if (saving) {
      return;
    }

    props.onClose();
  }

  async function handleSave() {
    if (campaignId === null || saving) {
      return;
    }

    const validated = validateEmailBuilderTemplateName(name);
    if (validated.ok === false) {
      setValidationError(t(validated.error));
      return;
    }

    setValidationError(null);
    setSaving(true);

    try {
      const flushResult = await emailBuilderCommitAndFlushDraft({
        dispatch,
        getAutoSaveStatus: () => store.getState().emailBuilderStore.autoSaveStatus,
        isFlushing: isFlushingServerDraftRef.current,
        setFlushing: flushing => {
          isFlushingServerDraftRef.current = flushing;
        },
      });

      if (flushResult === "ignored") {
        return;
      }

      if (flushResult === "failed") {
        setValidationError(t("Could not save draft before creating template."));
        return;
      }

      const latestDraft = store.getState().emailBuilderStore.draft;
      if (latestDraft === null) {
        setValidationError(t("Could not save template."));
        return;
      }

      const res = await PostEmailMarketingTemplate(
        {
          name: validated.name,
          campaign_id: campaignId,
          revision: latestDraft.revision,
        },
        false,
      );

      if (res.status !== StatusCode.OK || res.data?.data == null) {
        setValidationError(
          apiErrorMessage(res.message, t("Could not save template.")),
        );
        return;
      }

      props.onSaved();
      props.onClose();
    } finally {
      setSaving(false);
    }
  }

  const field = (
    <div className="email-builder-save-as-template-field">
      <Input
        label={t("Template name")}
        placeholder={t("e.g. Monthly Newsletter Layout")}
        value={name}
        maxLength={EMAIL_BUILDER_TEMPLATE_NAME_MAX_LENGTH}
        error={validationError !== null}
        disabled={saving}
        inputRef={inputRef}
        onChange={event => {
          setName(event.target.value);
          if (validationError !== null) {
            setValidationError(null);
          }
        }}
        onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
          if (event.key === "Enter") {
            event.preventDefault();
            void handleSave();
          }
        }}
      />
      {validationError !== null ? (
        <div className="email-builder-save-as-template-error" role="alert">
          {validationError}
        </div>
      ) : null}
    </div>
  );

  if (isMobile) {
    return (
      <Sheet
        open={props.open}
        size="small"
        title={t("Save as template")}
        supportText={t("Save this design as a template so you can reuse it later.")}
        closable
        onCancel={handleClose}
        cancelText={t("Cancel")}
        okText={t("Save template")}
        onOk={() => {
          void handleSave();
        }}
        okLoading={saving}
        okDisabled={saving || campaignId === null}
        cancelDisabled={saving}
        backDropCancel={!saving}
      >
        {field}
      </Sheet>
    );
  }

  return (
    <Popup
      open={props.open}
      type="success"
      title={t("Save as template")}
      description={t("Save this design as a template so you can reuse it later.")}
      closable
      onCancel={handleClose}
      cancelText={t("Cancel")}
      okText={saving ? t("Saving…") : t("Save template")}
      onOk={() => {
        void handleSave();
      }}
      okDisabled={saving || campaignId === null}
      cancelDisabled={saving}
      backDropCancel={!saving}
    >
      {field}
    </Popup>
  );
}
